import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/* iOS native shell signal — the server filters explicit content for the
 * Capacitor app. Inside the real app the WebKit bridge always exists, so
 * this header is always sent; the server also has a WKWebView UA backstop. */
api.interceptors.request.use((config) => {
  try {
    if (window.Capacitor?.isNativePlatform?.() || window.webkit?.messageHandlers?.bridge) {
      config.headers["X-Panda-Shell"] = "ios";
    }
  } catch (_) { /* never block a request over this */ }
  return config;
});

/* ---------- Silent refresh interceptor ----------
 * When any request returns 401, try /auth/refresh ONCE and retry the original.
 * Requests that themselves are auth flows (/auth/refresh, /auth/login, /auth/register)
 * are excluded to avoid loops. Multiple concurrent 401s share the same in-flight
 * refresh so we don't hammer the endpoint. If refresh fails, we surface the original 401.
 */
let refreshInFlight = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;
    const url = original.url || "";

    // Skip retry for the auth endpoints themselves
    const skip =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/logout");

    if (status === 401 && !original._retriedAfterRefresh && !skip) {
      original._retriedAfterRefresh = true;
      try {
        if (!refreshInFlight) {
          refreshInFlight = api
            .post("/auth/refresh")
            .finally(() => { refreshInFlight = null; });
        }
        await refreshInFlight;
        return api(original); // retry original request
      } catch (_) {
        // fall through — reject with the original 401
      }
    }
    if (status === 403 && error?.response?.data?.detail?.code === "age_verification_required" && !window.location.pathname.startsWith("/verify")) {
      window.location.assign("/verify");
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function extractError(e) {
  return formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Something went wrong.";
}
